import {
  ChevronDown,
  ChevronUp,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  fetchSocialLinks,
  SocialLink,
} from "@/services/landing/socialLinksService";

const footerLinks = [
  {
    links: [
      { label: "Community Impacts", path: "/pages/about-us/for-communities" },
    ],
    title: "Services",
  },
  {
    links: [
      { label: "Reach out to Us", path: "/Contact-us" },
    ],
    title: "Partner with Us",
  },
  {
    links: [
      { label: "Who We Are", path: "/pages/about-us/who-we-are" },
    ],
    title: "About Us",
  },
  {
    links: [
      { label: "Careers", path: "/pages/about-us/careers" },
    ],
    title: "Join Our Team",
  },
  {
    links: [
      {label:   "login now", path: "/auth/login" },
    ],
    title: "Dashboard",
  },
];

const legalLinks = [
  { label: "Privacy Policy", path: "/pages/legal/privacy-policy" },
  { label: "Report Issue", path: "/support/report-issue" },
];

export default function Footer() {
  const [open, setOpen] = useState<null | number>(null);
  const [socials, setSocials] = useState<SocialLink[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const loadSocialLinks = async () => {
      const links = await fetchSocialLinks();
      setSocials(links.filter((link) => link.is_active));
    };
    loadSocialLinks();
  }, []);

  const getSocialIcon = (iconName: string) => {
    // Try to get Lucide icon first
    const LucideIcon = (LucideIcons as any)[iconName];
    if (LucideIcon) {
      return { Icon: LucideIcon, type: "lucide" };
    }
    // Fallback to Font Awesome
    const faIcon = iconName.toLowerCase();
    return { icon: faIcon, type: "fontawesome" };
  };

  const handleLinkClick = (path: string) => {
    // Check if it's an external link or internal route
    if (path.startsWith("http")) {
      window.open(path, "_blank", "noopener,noreferrer");
    } else {
      navigate(path);
    }
  };

  const handleAppDownload = (type: "eats" | "rider") => {
    if (type === "rider") {
      navigate("/pages/catch-rides");
    } else {
      navigate("/pages/eats");
    }
  };

  return (
    <footer className="bg-base-300 px-4 pb-4 pt-12 text-base-content">
      {/* Desktop */}
      <div className="mx-auto hidden max-w-7xl grid-cols-2 gap-8 border-b border-base-content/20 pb-8 md:grid lg:grid-cols-5">
        {footerLinks.map((section) => (
          <div key={section.title}>
            <div className="mb-2 font-bold">{section.title}</div>
            <ul className="space-y-1">
              {section.links.map((link) => (
                <li key={link.label}>
                  <button
                    className="text-left text-base-content/80 transition-colors hover:text-primary hover:underline"
                    onClick={() => handleLinkClick(link.path)}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Mobile Collapsible */}
      <div className="border-b border-base-content/20 pb-4 md:hidden">
        {footerLinks.map((section, idx) => (
          <div className="mb-2" key={section.title}>
            <button
              className="flex w-full items-center justify-between py-2 font-bold"
              onClick={() => setOpen(open === idx ? null : idx)}
            >
              {section.title}
              {open === idx ? (
                <ChevronUp size={18} />
              ) : (
                <ChevronDown size={18} />
              )}
            </button>
            {open === idx && (
              <ul className="space-y-1 pb-2 pl-2">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <button
                      className="text-left text-base-content/80 transition-colors hover:text-primary hover:underline"
                      onClick={() => handleLinkClick(link.path)}
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      {/* Bottom bar */}
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 pt-6 md:flex-row">
        {/* Language and Branding */}
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-2">
            <img
              alt="Lesotho"
              className="h-4 w-6 rounded"
              src="https://flagpedia.net/data/flags/w580/ls.webp"
            />
            EN
          </span>
          <button
            className="ml-4 text-xl font-bold transition-colors hover:text-primary"
            onClick={() => navigate("/")}
          >
            Harehloekiseng
          </button>

          {/* Socials */}
          <div className="ml-4 flex gap-3">
            {socials.map((social) => {
              const iconData = getSocialIcon(social.icon);
              return (
                <a
                  aria-label={social.name}
                  className="text-lg transition-colors hover:text-primary"
                  href={social.url}
                  key={social.id}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {iconData.type === "lucide" ? (
                    <iconData.Icon size={18} />
                  ) : (
                    <i className={`fa-brands fa-${iconData.icon}`}></i>
                  )}
                </a>
              );
            })}
          </div>
        </div>

        {/* App Buttons */}
        <div className="mt-4 flex gap-2 md:mt-0">
        <div className="mt-4 flex gap-2 md:mt-0">
          <button
            className="btn btn-outline btn-primary btn-sm flex items-center gap-1"
            onClick={() => window.scrollTo({ behavior: "smooth", top: 0 })}
          >
            <LucideIcons.ArrowUp className="h-4 w-4" />
            Back to Top
          </button>
        </div>
          {/* <button
            className="btn btn-outline btn-primary btn-sm"
            onClick={() => handleAppDownload("eats")}
          >
            Try Hareeng Eats
          </button> */}
        </div>
      </div>

      {/* Legal Section */}
      <div className="mx-auto mt-6 flex max-w-7xl flex-col items-center justify-between gap-2 text-xs text-base-content/60 md:flex-row">
        <div className="flex flex-wrap gap-4">
          {legalLinks.map((link) => (
            <button
              className="transition-colors hover:text-primary"
              key={link.label}
              onClick={() => handleLinkClick(link.path)}
            >
              {link.label}
            </button>
          ))}
        </div>
        <div className="mt-2 md:mt-0">
          &copy; {new Date().getFullYear()} Hareeng Mobile Technology . Designed by Bokang Mothomotsoana
        </div>
      </div>
    </footer> 
  );
}
